import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { without } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { useModalContext } from '@magoosh/context/modal_context';
import { DangerButton, DefaultFilledButton, PrimaryButton } from '@magoosh/lib/button';
import Draggable from '@magoosh/lib/draggable';
import FieldInput from '@magoosh/lib/formik_inputs/field_input';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import StatusBadge from '@magoosh/organisms/status_badge';
import { adminPaths } from 'config/path_helpers';
import fetch from 'utilities/fetch';

import ContextualLink from '@admin/app/components/contextual_link';

import RequestContentFixModal from '../request_content_fix_modal';
import TextPreview from '../text_preview';
import { DraftPrompt } from '../../types';

interface Props {
  draftPrompt: DraftPrompt;
  isEditable: boolean;
  onChange?: (draftPrompt: DraftPrompt) => void;
}

const DraftPromptPreview: React.FC<Props> = (props) => {
  const { setModal } = useModalContext();
  const [isEditing, setIsEditing] = useState(false);

  const handleAnswerChoiceAdded = (groupIndex: number, setFieldValue, values) => {
    const updatedAnswerChoiceGroup = [
      ...values.answerChoiceGroups[groupIndex],
      { correct: false, value: ''}
    ];

    const result = [...values.answerChoiceGroups];
    result[groupIndex] = updatedAnswerChoiceGroup;

    setFieldValue('answerChoiceGroups', result);
  };

  const handleAnswerChoiceOrderChanged = (groupIndex: number, fromIndex: number, toIndex: number, setFieldValue, values) => {
    const answerChoiceGroup = values.answerChoiceGroups[groupIndex];
    const answerChoice = answerChoiceGroup[fromIndex];
    const updatedAnswerChoices = without(answerChoiceGroup, answerChoice);
    const updatedAnswerChoiceGroup = [
      ...updatedAnswerChoices.slice(0, toIndex),
      answerChoice,
      ...updatedAnswerChoices.slice(toIndex)
    ];

    const result = [...values.answerChoiceGroups];
    result[groupIndex] = updatedAnswerChoiceGroup;

    setFieldValue('answerChoiceGroups', result);
  };

  const handleAnswerChoiceRemoved = (groupIndex: number, index: number, setFieldValue, values) => {
    const answerChoiceGroup = values.answerChoiceGroups[groupIndex];
    const answerChoice = answerChoiceGroup[index];
    const updatedAnswerChoices = without(answerChoiceGroup, answerChoice);

    const result = [...values.answerChoiceGroups];
    result[groupIndex] = updatedAnswerChoices;

    setFieldValue('answerChoiceGroups', result);
  };

  const handleSubmit = (values, { setErrors, setSubmitting }) => {
    setSubmitting(true)

    fetch(adminPaths.api.testPrep.aiDraftPrompt(props.draftPrompt.id), {
      method: 'PUT',
      body: JSON.stringify(values)
    }).then((response) => {
      setIsEditing(false);
      setSubmitting(false);

      props.onChange(response.draftPrompt);
    }).catch((error) => {
      setErrors(error.errors);
      setSubmitting(false);
    });
  };

  const presentContentFixModal = () => {
    setModal({
      header: 'Request a Fix',
      body: (
        <RequestContentFixModal
          draftPromptId={props.draftPrompt.id}
          onClose={() => setModal(null)}
          onSuccess={props.onChange}
        />
      ),
      size: 'lg'
    });
  };

  const promptContentPreview = () => {
    if (!props.draftPrompt.text) return null;

    if (props.draftPrompt.questionType === 'Math - Quantitative Comparison' || props.draftPrompt.text.match(/(Quantity|Column) A/)) {
      try {
        const promptMatch = props.draftPrompt.text.match(/([\s\S]+?)(Quantity|Column) A/);
        const promptContent = promptMatch ? promptMatch[1].trim() : '';
        const quantityA = props.draftPrompt.text.match(/(Quantity|Column) A([:\s]*)([\s\S]+?)(Quantity|Column) B/)[3].trim();
        const quantityB = props.draftPrompt.text.match(/(Quantity|Column) B([:\s]*)([\s\S]+)/)[3].trim();

        return (
          <div>
            <TextPreview text={promptContent} />

            <table className="table u-margin-T-s">
              <tbody>
                <tr>
                  <th>Quantity A</th>
                  <th>Quantity B</th>
                </tr>

                <tr>
                  <td><TextPreview text={quantityA} /></td>
                  <td><TextPreview text={quantityB} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      } catch {
        return <TextPreview text={props.draftPrompt.text} />;
      }
    } else {
      return <TextPreview text={props.draftPrompt.text} />;
    }
  };

  const resultingPromptPath = () => {
    if (props.draftPrompt.resultingPrompt.type === 'Prompt') {
      return adminPaths.prompt(props.draftPrompt.resultingPrompt.id);
    } else {
      return '#';
    }
  };


  const updateStatus = (status: string) => {
    fetch(adminPaths.api.testPrep.aiDraftPrompt(props.draftPrompt.id), {
      method: 'PUT',
      body: JSON.stringify({ status })
    }).then((response) => {
      props.onChange && props.onChange(response.draftPrompt);
    });
  };

  const initialValues = {
    answerChoiceGroups: props.draftPrompt.answerChoiceGroups,
    explanationText: props.draftPrompt.explanationText.text,
    text: props.draftPrompt.text
  }

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      {({ isSubmitting, resetForm, setFieldValue, submitForm , values}) => (
        <Form>
          <div className="d-flex flex-column gap-l">
            <div className="align-items-center d-flex gap-s">
              <div className="align-items-center d-flex flex-1 gap-xs">
                <div><strong>Draft Prompt</strong> <small>({props.draftPrompt.id})</small></div>
                {
                  props.draftPrompt.resultingPrompt && (
                    <>
                      <FontAwesomeIcon className="small" icon="arrow-right" />
                      <ContextualLink href={resultingPromptPath()}>
                        {props.draftPrompt.resultingPrompt.type} ({props.draftPrompt.resultingPrompt.id})
                      </ContextualLink>
                    </>
                  )
                }
              </div>

              {
                props.isEditable && (
                  <div className="align-items-center d-flex gap-s u-margin-R-xl">
                    <div>
                      <StatusBadge status={props.draftPrompt.status} />
                    </div>

                    <div>
                      <a className="text-gray-light" onClick={() => updateStatus('accepted')}>Accept</a>
                      {' | '}
                      <a className="text-gray-light" onClick={() => updateStatus('draft')}>Draft</a>
                      {' | '}
                      <a className="text-gray-light" onClick={() => updateStatus('rejected')}>Reject</a>
                    </div>
                  </div>
                )
              }

              {
                props.draftPrompt.audits.map((audit) => (
                  <div key={audit.id}>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-audit-${audit.id}`}>
                          {audit.result?.reasoning || audit.result?.audit}
                        </Tooltip>
                      }
                    >
                      <div><StatusBadge status={audit.status} text={audit.name} /></div>
                    </OverlayTrigger>
                  </div>
                ))
              }

              {
                props.isEditable && (
                  isEditing ? (
                    <>
                      <PrimaryButton onClick={submitForm} size="sm" submitting={isSubmitting}>
                        Save Edits
                      </PrimaryButton>
                      <DangerButton onClick={() => { resetForm(); setIsEditing(false); }} size="sm">
                        Discard Edits
                      </DangerButton>
                    </>

                  ) : (
                    <>
                      <DefaultFilledButton onClick={presentContentFixModal} size="sm">
                        Request Fix
                      </DefaultFilledButton>
                      <DefaultFilledButton onClick={() => setIsEditing(true)} size="sm">
                        Edit
                      </DefaultFilledButton>
                    </>
                  )
                )
              }
            </div>

            <div className="d-flex gap-m">
              <div className="d-flex flex-1 flex-column gap-s">
                <div className="well well-highlight">
                  {
                    isEditing ? (
                      <FieldInput hideLabel name="text" type="textarea" style={{ height: 200 }} />
                    ) : (
                      promptContentPreview()
                    )
                  }
                </div>
              </div>

              <div className="d-flex flex-1 flex-column gap-s">
                <div className="d-flex flex-1 gap-m">
                  {
                    values.answerChoiceGroups.map((answerChoiceGroup, groupIdx) => (
                      <div key={groupIdx} className="d-flex flex-1 flex-column gap-s">
                        <Draggable
                          onDrop={(fromIndex, toIndex) => {
                            handleAnswerChoiceOrderChanged(groupIdx, fromIndex, toIndex, setFieldValue, values)
                          }}
                        >
                          {
                            answerChoiceGroup.map((answerChoice, idx) => (
                              <div key={idx}>
                                <div className="align-items-center d-flex gap-xs well well-hollow">
                                  {isEditing && <FontAwesomeIcon className="text-gray-light" icon="bars" style={{ cursor: 'grab' }} />}

                                  <div className="align-items-center d-flex flex-1 gap-xs">
                                    {
                                      isEditing ? (
                                        <>
                                          <div style={{ minWidth: 10 }}>
                                            <FieldInput hideLabel name={`answerChoiceGroups[${groupIdx}][${idx}].correct`} type="checkbox" />
                                          </div>
                                          <div className="flex-1">
                                            <FieldInput hideLabel name={`answerChoiceGroups[${groupIdx}][${idx}].value`} type="text" />
                                          </div>
                                          <a onClick={() => handleAnswerChoiceRemoved(groupIdx, idx, setFieldValue, values)}>
                                            <FontAwesomeIcon className="text-danger" icon="minus-circle" />
                                          </a>
                                        </>
                                      ) : (
                                        <>
                                          <div style={{ minWidth: 10 }}>
                                            {answerChoice.correct && <FontAwesomeIcon className="text-primary" icon="check" />}
                                          </div>
                                          <TextPreview text={answerChoice.value} />
                                        </>
                                      )
                                    }
                                  </div>
                                </div>
                              </div>
                            ))
                          }

                          {
                            isEditing && (
                              <a onClick={() => handleAnswerChoiceAdded(groupIdx, setFieldValue, values)}>
                                <FontAwesomeIcon className="text-success" icon="plus-circle" /> Add
                              </a>
                            )
                          }
                        </Draggable>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>

            {
              props.draftPrompt.explanationText && (
                <div className="d-flex flex-1 flex-column gap-s">
                  {
                    isEditing ? (
                      <FieldInput hideLabel name="explanationText" type="textarea" style={{ height: 400 }} />
                    ) : (
                      <TextPreview text={props.draftPrompt.explanationText.html}/>
                    )
                  }
                </div>
              )
            }
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default DraftPromptPreview;
